import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  Grid, 
  TextField, 
  Button, 
  FormControlLabel, 
  Checkbox,
  Paper,
  Divider
} from '@mui/material';
import { API_URL } from '../../config/api';

const validationSchema = yup.object({
  confirmation: yup.boolean().oneOf([true], 'You must confirm the declaration').required('You must confirm the declaration'),
  dateOfDeclaration: yup.date().required('Date of declaration is required')
});

const DeclarationStep = ({ onFormSubmit, readOnly = false, userIdOverride }) => {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const effectiveUserId = userIdOverride || user?.id;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      confirmation: false,
      dateOfDeclaration: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        let response;
        
        if (isUpdate) {
          // Update existing declaration
          const url = `${API_URL}/offboarding/declaration/user/${effectiveUserId}`;
          response = await axios.put(url, values, {
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json'
            }
          });
          toast.success('Declaration updated successfully!');
        } else {
          // Create new declaration
          const url = `${API_URL}/offboarding/declaration`;
          response = await axios.post(url, values, {
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json'
            }
          });
          toast.success('Declaration submitted successfully!');
        }
        
        // After successful submission, fetch the updated data to set update mode
        try {
          const fetchResponse = await axios.get(`${API_URL}/offboarding/declaration/user/${effectiveUserId}`, {
            headers: getAuthHeaders()
          });
          
          if (fetchResponse.data) {
            const formattedData = {
              ...fetchResponse.data,
              dateOfDeclaration: fetchResponse.data.dateOfDeclaration ? new Date(fetchResponse.data.dateOfDeclaration).toISOString().split('T')[0] : ''
            };
            formik.setValues(formattedData);
            setIsUpdate(true);
            console.log('Form updated to update mode after submission');
            
            // Trigger validation update in parent component
            if (onFormSubmit) {
              onFormSubmit();
            }
          }
        } catch (fetchError) {
          console.error('Error fetching updated data:', fetchError);
        }
        
      } catch (error) {
        const errorMessage = error.response?.data?.error || `Failed to ${isUpdate ? 'update' : 'submit'} declaration`;
        toast.error(errorMessage);
        console.error(`Error ${isUpdate ? 'updating' : 'submitting'} declaration:`, error);
      } finally {
        setLoading(false);
      }
    }
  });

  // Load existing declaration data if available
  useEffect(() => {
    if (effectiveUserId) {
      console.log('Fetching declaration data for user:', effectiveUserId);
      axios.get(`${API_URL}/offboarding/declaration/user/${effectiveUserId}`, {
        headers: getAuthHeaders()
      })
        .then(response => {
          console.log('GET API Response:', response.data);
          const data = response.data.data || response.data; // Handle both response formats
          if (data) {
            console.log('Raw data:', data);
            console.log('dateOfDeclaration value:', data.dateOfDeclaration);
            const formattedData = {
              ...data,
              dateOfDeclaration: data.dateOfDeclaration ? new Date(data.dateOfDeclaration).toISOString().split('T')[0] : ''
            };
            console.log('Formatted data:', formattedData);
            formik.setValues(formattedData);
            setIsUpdate(true);
          }
        })
        .catch(error => {
          console.log('GET API Error:', error.response?.status, error.response?.data);
          if (error.response && error.response.status !== 404) {
            console.error('Error fetching declaration form:', error);
            toast.error('Error loading existing form data');
          }
        });
    }
  }, [effectiveUserId, getAuthHeaders]);

  return (
    <Paper elevation={3} sx={{ p: 3, m: 2, backgroundColor: 'white' }}>
      <form onSubmit={formik.handleSubmit}>
        <Typography variant="h4" sx={{ mb: 3, textAlign: 'center', fontWeight: 'bold' }}>
          DECLARATION
        </Typography>
        
        {/* Declaration Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Employee Declaration
          </Typography>
          {isUpdate ? (
            <Box sx={{ 
              mb: 3, 
              p: 3, 
              backgroundColor: '#e8f5e8', 
              borderRadius: 2, 
              border: '1px solid #4caf50',
              textAlign: 'center'
            }}>
              <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 'bold', mb: 1 }}>
                ✓ Declaration Submitted Successfully
              </Typography>
              <Typography variant="body1" sx={{ color: '#2e7d32', fontWeight: 'medium' }}>
                Submitted on: {formik.values.dateOfDeclaration}
              </Typography>
              <Typography variant="body2" sx={{ color: '#2e7d32', mt: 1, fontStyle: 'italic' }}>
                You have confirmed that you have completed all necessary offboarding procedures and understand the terms of your departure from the organization.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 3, fontStyle: 'italic' }}>
                Please read the following declaration carefully and confirm your understanding by checking the box below.
              </Typography>
              
              <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="confirmation"
                        checked={formik.values.confirmation}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    }
                    label="I have confirmed that I have completed all necessary offboarding procedures and understand the terms of my departure from the organization."
                  />
                  {formik.touched.confirmation && Boolean(formik.errors.confirmation) && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      {formik.errors.confirmation}
                    </Typography>
                  )}
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Date of Declaration"
                    name="dateOfDeclaration"
                    type="date"
                    value={formik.values.dateOfDeclaration}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    required
                    error={formik.touched.dateOfDeclaration && Boolean(formik.errors.dateOfDeclaration)}
                    helperText={formik.touched.dateOfDeclaration && formik.errors.dateOfDeclaration}
                  />
                </Grid>
              </Grid>
              </fieldset>
            </>
          )}
        </Box>

        {!isUpdate && !readOnly && (
          <>
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ minWidth: 150, py: 1.5 }}
              >
                {loading ? 'Submitting...' : 'Submit Declaration'}
              </Button>
            </Box>
          </>
        )}
      </form>
    </Paper>
  );
};

export default DeclarationStep;